// 路线详情模块
// 负责处理路线详情弹窗的显示、地图初始化、预订和分享功能

// Mapbox访问令牌 (需要替换为实际的令牌)
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoieGlhb2thbmciLCJhIjoiY2lqc2d2NXlyMGhkbHU0bTVtcGNiOWxseCJ9.J5qsX13KKNT1slMGS-MOLg';

/**
 * 查看路线详情
 * @param {string} routeId - 路线ID
 * @param {Array} routesData - 路线数据数组
 * @param {string} currentLanguage - 当前语言
 * @param {Function} getTranslation - 获取翻译文本的函数
 */
function viewRouteDetails(routeId, routesData, currentLanguage, getTranslation) {
    const route = routesData.find(r => r.id === routeId);
    if (!route) {
        console.error('路线未找到:', routeId);
        return;
    }
    
    // 创建详情模态框
    createRouteModal(route, currentLanguage, getTranslation);
}

/**
 * 创建路线详情模态框
 * @param {Object} route - 路线数据
 * @param {string} currentLanguage - 当前语言
 * @param {Function} getTranslation - 获取翻译文本的函数
 */
function createRouteModal(route, currentLanguage, getTranslation) {
    // 移除现有模态框
    const existingModal = document.getElementById('route-modal');
    if (existingModal) {
        existingModal.remove();
    }
    
    const name = route.name[currentLanguage] || route.name.zh;
    const location = route.location[currentLanguage] || route.location.zh;
    const description = route.description[currentLanguage] || route.description.zh;
    const highlights = route.highlights[currentLanguage] || route.highlights.zh;
    const bestSeason = route.bestSeason[currentLanguage] || route.bestSeason.zh;
    const difficultyText = getTranslation(`routes.difficulty.${route.difficulty}`) || route.difficulty;
    
    const modal = document.createElement('div');
    modal.id = 'route-modal';
    modal.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50';
    modal.innerHTML = `
        <div class="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div class="relative">
                <img src="${route.images[0]}" alt="${name}" class="w-full h-64 object-cover rounded-t-2xl" onerror="this.src='/assets/placeholder.jpg'">
                <button class="absolute top-4 right-4 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all" onclick="RouteDetails.closeModal()">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
            <div class="p-6">
                <div class="flex justify-between items-start mb-4">
                    <div>
                        <h2 class="text-3xl font-bold text-neutral-900 mb-2">${name}</h2>
                        <p class="text-lg text-neutral-600">${location}</p>
                    </div>
                    <div class="text-right">
                        <div class="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-semibold mb-2">
                            ${difficultyText}
                        </div>
                        <div class="text-2xl font-bold text-primary-600">¥${route.price.amount}</div>
                    </div>
                </div>
                
                <div class="grid md:grid-cols-4 gap-4 mb-6">
                    <div class="text-center p-4 bg-neutral-50 rounded-lg">
                        <div class="text-2xl font-bold text-neutral-900">${route.duration.days}</div>
                        <div class="text-sm text-neutral-600">${getTranslation('units.days') || '天'}</div>
                    </div>
                    <div class="text-center p-4 bg-neutral-50 rounded-lg">
                        <div class="text-2xl font-bold text-neutral-900">${route.distance}</div>
                        <div class="text-sm text-neutral-600">${getTranslation('units.km') || 'km'}</div>
                    </div>
                    <div class="text-center p-4 bg-neutral-50 rounded-lg">
                        <div class="text-2xl font-bold text-neutral-900">${route.elevation.highest}</div>
                        <div class="text-sm text-neutral-600">${getTranslation('routes.elevation') || '最高海拔'}</div>
                    </div>
                    <div class="text-center p-4 bg-neutral-50 rounded-lg">
                        <div class="text-sm font-semibold text-neutral-900">${bestSeason}</div>
                        <div class="text-sm text-neutral-600">${getTranslation('routes.bestSeason') || '最佳季节'}</div>
                    </div>
                </div>
                
                <div class="mb-6">
                    <h3 class="text-xl font-bold text-neutral-900 mb-3">路线介绍</h3>
                    <p class="text-neutral-700 leading-relaxed">${description}</p>
                </div>
                
                <div class="mb-6">
                    <h3 class="text-xl font-bold text-neutral-900 mb-3">路线亮点</h3>
                    <ul class="space-y-2">
                        ${highlights.map(highlight => `<li class="flex items-start space-x-2">
                            <svg class="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                            <span class="text-neutral-700">${highlight}</span>
                        </li>`).join('')}
                    </ul>
                </div>
                
                <div class="mb-6">
                    <h3 class="text-xl font-bold text-neutral-900 mb-3">路线地图</h3>
                    ${route.coordinates.komoot ? 
                        `<div class="mb-4">
                            <div class="komoot-info">
                                <div class="flex items-center justify-between mb-2">
                                    <h4 class="font-semibold text-neutral-900">专业轨迹数据</h4>
                                    <span class="komoot-badge">Komoot提供</span>
                                </div>
                                <p class="text-sm text-neutral-600">查看详细的海拔、距离和路线信息</p>
                            </div>
                            <div class="komoot-embed">
                                <iframe src="${route.coordinates.komoot}" width="100%" height="500" frameborder="0" scrolling="no"></iframe>
                            </div>
                        </div>` : 
                        `<div id="route-map" class="map-container"></div>`
                    }
                </div>
                
                ${route.videos ? `
                <div class="mb-6">
                    <h3 class="text-xl font-bold text-neutral-900 mb-3">视频介绍</h3>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        ${route.videos.youtube ? `
                        <div class="video-container">
                            <div class="bg-neutral-50 rounded-lg p-4 mb-3">
                                <div class="flex items-center justify-between mb-2">
                                    <h4 class="font-semibold text-neutral-900">${route.videos.youtube.title[currentLanguage] || route.videos.youtube.title.zh}</h4>
                                    <span class="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">YouTube</span>
                                </div>
                            </div>
                            <div class="video-embed">
                                <iframe 
                                    width="100%" 
                                    height="315" 
                                    src="https://www.youtube.com/embed/${route.videos.youtube.id}?si=-SwE5bCrDFdTwO_h" 
                                    title="YouTube video player" 
                                    frameborder="0" 
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                    referrerpolicy="strict-origin-when-cross-origin" 
                                    allowfullscreen>
                                </iframe>
                            </div>
                        </div>
                        ` : ''}
                        
                        ${route.videos.bilibili ? `
                        <div class="video-container">
                            <div class="bg-neutral-50 rounded-lg p-4 mb-3">
                                <div class="flex items-center justify-between mb-2">
                                    <h4 class="font-semibold text-neutral-900">${route.videos.bilibili.title[currentLanguage] || route.videos.bilibili.title.zh}</h4>
                                    <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">哔哩哔哩</span>
                                </div>
                            </div>
                            <div class="video-embed">
                                <iframe 
                                    src="//player.bilibili.com/player.html?isOutside=true&aid=${route.videos.bilibili.aid}&bvid=${route.videos.bilibili.bvid}&cid=${route.videos.bilibili.cid}&p=1" 
                                    width="100%" 
                                    height="315" 
                                    scrolling="no" 
                                    border="0" 
                                    frameborder="no" 
                                    framespacing="0" 
                                    allowfullscreen="true">
                                </iframe>
                            </div>
                        </div>
                        ` : ''}
                    </div>
                </div>
                ` : ''}
                
                <!-- 用户评论区域 -->
                <div class="mb-6">
                    <h3 class="text-xl font-bold text-neutral-900 mb-3">用户评价</h3>
                    <div id="route-comments-${route.id}" class="comments-container">
                        <!-- 评论内容将通过JavaScript动态加载 -->
                    </div>
                </div>
                
                <div class="flex flex-col sm:flex-row gap-4">
                    <button class="btn btn-primary btn-lg flex-1" onclick="RouteDetails.bookRoute('${route.id}')">
                        ${getTranslation('routes.bookNow') || '立即预订'}
                    </button>
                    <button class="btn btn-secondary btn-lg" onclick="RouteDetails.shareRoute('${route.id}')">
                        ${getTranslation('common.share') || '分享'}
                    </button>
                    <button class="btn btn-ghost btn-lg" onclick="RouteDetails.openInApp('${route.id}')">
                        📱 在App中导航
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    
    // 初始化地图
    setTimeout(() => {
        // 只在没有Komoot轨迹时才初始化Mapbox地图
        if (!route.coordinates.komoot) {
            initializeRouteMap(route, currentLanguage);
        }
        
        // 初始化评论系统
        if (window.commentSystem) {
            window.commentSystem.renderComments(route.id);
        }
    }, 100);
    
    // 阻止背景滚动
    document.body.style.overflow = 'hidden';
}

/**
 * 关闭路线模态框
 */
function closeRouteModal() {
    const modal = document.getElementById('route-modal');
    if (modal) {
        modal.remove();
        document.body.style.overflow = '';
    }
}

/**
 * 初始化路线地图
 * @param {Object} route - 路线数据
 * @param {string} currentLanguage - 当前语言
 */
function initializeRouteMap(route, currentLanguage) {
    const mapContainer = document.getElementById('route-map');
    if (!mapContainer || !route.coordinates) {
        return;
    }
    
    try {
        // 注意：这里需要实际的Mapbox访问令牌
        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
        
        const map = new mapboxgl.Map({
            container: 'route-map',
            style: 'mapbox://styles/mapbox/outdoors-v12',
            center: route.coordinates.center,
            zoom: 10
        });
        
        // 地图加载完成后添加路线轨迹
        map.on('load', async () => {
            await loadRouteTrack(map, route);
        });
        
        // 添加起点标记
        new mapboxgl.Marker({ color: '#2d9f63' })
            .setLngLat(route.coordinates.start)
            .setPopup(new mapboxgl.Popup().setHTML(`<h3>起点</h3><p>${route.name[currentLanguage] || route.name.zh}</p>`))
            .addTo(map);
        
        // 添加终点标记
        new mapboxgl.Marker({ color: '#dc2626' })
            .setLngLat(route.coordinates.end)
            .setPopup(new mapboxgl.Popup().setHTML(`<h3>终点</h3><p>${route.name[currentLanguage] || route.name.zh}</p>`))
            .addTo(map);
        
        // 添加导航控件
        map.addControl(new mapboxgl.NavigationControl());
        
    } catch (error) {
        console.error('地图初始化失败:', error);
        mapContainer.innerHTML = '<div class="flex items-center justify-center h-full text-neutral-500">地图加载失败</div>';
    }
}

/**
 * 加载路线轨迹
 * @param {Object} map - Mapbox地图实例
 * @param {Object} route - 路线数据
 */
async function loadRouteTrack(map, route) {
    if (!route.coordinates.routes) {
        console.log('该路线没有轨迹文件');
        return;
    }
    
    try {
        // 加载GeoJSON轨迹文件
        const response = await fetch(`/data/${route.coordinates.routes}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const geojsonData = await response.json();
        
        // 添加轨迹数据源
        map.addSource('route-track', {
            'type': 'geojson',
            'data': geojsonData
        });
        
        // 添加轨迹线条图层
        map.addLayer({
            'id': 'route-track-line',
            'type': 'line',
            'source': 'route-track',
            'layout': {
                'line-join': 'round',
                'line-cap': 'round'
            },
            'paint': {
                'line-color': '#dc2626', // 红色
                'line-width': 4,
                'line-opacity': 0.8
            }
        });
        
        // 添加轨迹点图层（可选，显示轨迹点）
        map.addLayer({
            'id': 'route-track-points',
            'type': 'circle',
            'source': 'route-track',
            'paint': {
                'circle-radius': 3,
                'circle-color': '#dc2626',
                'circle-opacity': 0.6
            }
        });
        
        // 调整地图视图以适应轨迹
        const bounds = new mapboxgl.LngLatBounds();
        
        // 遍历GeoJSON数据获取边界
        if (geojsonData.type === 'FeatureCollection') {
            geojsonData.features.forEach(feature => {
                if (feature.geometry.type === 'LineString') {
                    feature.geometry.coordinates.forEach(coord => {
                        bounds.extend(coord);
                    });
                } else if (feature.geometry.type === 'Point') {
                    bounds.extend(feature.geometry.coordinates);
                }
            });
        } else if (geojsonData.type === 'Feature') {
            if (geojsonData.geometry.type === 'LineString') {
                geojsonData.geometry.coordinates.forEach(coord => {
                    bounds.extend(coord);
                });
            } else if (geojsonData.geometry.type === 'Point') {
                bounds.extend(geojsonData.geometry.coordinates);
            }
        }
        
        // 如果有有效边界，调整地图视图
        if (!bounds.isEmpty()) {
            map.fitBounds(bounds, {
                padding: 50,
                maxZoom: 14
            });
        }
        
        console.log('路线轨迹加载成功:', route.coordinates.routes);
        
    } catch (error) {
        console.error('加载路线轨迹失败:', error);
        // 轨迹加载失败不影响地图的基本功能
    }
}

/**
 * 预订路线
 * @param {string} routeId - 路线ID
 */
function bookRoute(routeId) {
    // 这里可以集成实际的预订系统
    alert('预订功能正在开发中，请通过联系我们页面获取更多信息！');
}

/**
 * 分享路线
 * @param {string} routeId - 路线ID
 * @param {Array} routesData - 路线数据数组
 * @param {string} currentLanguage - 当前语言
 */
function shareRoute(routeId, routesData, currentLanguage) {
    const route = routesData.find(r => r.id === routeId);
    if (!route) return;
    
    const name = route.name[currentLanguage] || route.name.zh;
    const shareData = {
        title: `${name} - 徒步中国`,
        text: `发现中国最美徒步路线：${name}`,
        url: `${window.location.origin}#${routeId}`
    };
    
    if (navigator.share) {
        navigator.share(shareData).catch(console.error);
    } else {
        // 后备方案：复制到剪贴板
        navigator.clipboard.writeText(shareData.url).then(() => {
            showToast('链接已复制到剪贴板！', 'success');
        }).catch(() => {
            showToast('分享失败，请手动复制链接', 'error');
        });
    }
}

/**
 * 在App中打开导航
 * @param {string} routeId - 路线ID
 * @param {Array} routesData - 路线数据数组
 * @param {string} currentLanguage - 当前语言
 */
function openInApp(routeId, routesData, currentLanguage) {
    const route = routesData.find(r => r.id === routeId);
    if (!route) {
        console.error('路线未找到:', routeId);
        return;
    }
    
    const name = route.name[currentLanguage] || route.name.zh;
    
    // 创建App导航对话框
    const modal = document.createElement('div');
    modal.id = 'app-navigation-modal';
    modal.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50';
    modal.innerHTML = `
        <div class="bg-white rounded-2xl max-w-md w-full p-6">
            <div class="text-center">
                <div class="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                    <svg class="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                    </svg>
                </div>
                <h3 class="text-xl font-bold text-neutral-900 mb-2">在HikeChina App中导航</h3>
                <p class="text-neutral-600 mb-4">使用HikeChina移动应用获得更好的导航体验</p>
                <div class="bg-neutral-50 rounded-lg p-4 mb-6">
                    <h4 class="font-semibold text-neutral-900 mb-2">${name}</h4>
                    <p class="text-sm text-neutral-600">包含完整的GPS轨迹、离线地图和实时导航功能</p>
                </div>
                <div class="space-y-3">
                    <button class="btn btn-primary w-full" onclick="RouteDetails.downloadApp()">
                        📱 下载HikeChina App
                    </button>
                    <button class="btn btn-secondary w-full" onclick="RouteDetails.copyTrackLink('${routeId}')">
                        🔗 复制轨迹链接
                    </button>
                    <button class="btn btn-ghost w-full" onclick="RouteDetails.closeAppModal()">
                        取消
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';
}

/**
 * 关闭App导航对话框
 */
function closeAppModal() {
    const modal = document.getElementById('app-navigation-modal');
    if (modal) {
        modal.remove();
        document.body.style.overflow = '';
    }
}

/**
 * 下载App（演示功能）
 */
function downloadApp() {
    showToast('HikeChina App正在开发中，敬请期待！', 'info');
    closeAppModal();
}

/**
 * 复制轨迹链接
 * @param {string} routeId - 路线ID
 * @param {Array} routesData - 路线数据数组
 */
function copyTrackLink(routeId, routesData) {
    const route = routesData.find(r => r.id === routeId);
    if (!route || !route.coordinates.routes) {
        showToast('该路线暂无轨迹文件', 'error');
        return;
    }
    
    const trackUrl = `${window.location.origin}/data/${route.coordinates.routes}`;
    
    navigator.clipboard.writeText(trackUrl).then(() => {
        showToast('轨迹文件链接已复制到剪贴板！', 'success');
        closeAppModal();
    }).catch(() => {
        showToast('复制失败，请手动复制链接', 'error');
    });
}

/**
 * 显示提示消息
 * @param {string} message - 消息内容
 * @param {string} type - 消息类型 ('success', 'error', 'info')
 */
function showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white transition-all duration-300 transform translate-x-full ${
        type === 'success' ? 'bg-green-500' :
        type === 'error' ? 'bg-red-500' :
        'bg-blue-500'
    }`;
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    // 显示动画
    setTimeout(() => {
        toast.classList.remove('translate-x-full');
    }, 100);
    
    // 自动隐藏
    setTimeout(() => {
        toast.classList.add('translate-x-full');
        setTimeout(() => {
            toast.remove();
        }, 300);
    }, 3000);
}

// 导出路线详情模块
const RouteDetails = {
    view: (routeId, routesData, currentLanguage, getTranslation) => 
        viewRouteDetails(routeId, routesData, currentLanguage, getTranslation),
    closeModal: closeRouteModal,
    bookRoute: bookRoute,
    shareRoute: (routeId, routesData, currentLanguage) => 
        shareRoute(routeId, routesData, currentLanguage),
    openInApp: (routeId, routesData, currentLanguage) => 
        openInApp(routeId, routesData, currentLanguage),
    closeAppModal: closeAppModal,
    downloadApp: downloadApp,
    copyTrackLink: (routeId, routesData) => 
        copyTrackLink(routeId, routesData)
};

// 兼容性：保持全局函数供HTML调用
window.RouteDetails = RouteDetails;
window.viewRouteDetails = viewRouteDetails;
window.closeRouteModal = closeRouteModal;
window.bookRoute = bookRoute;
window.shareRoute = shareRoute;
window.openInApp = openInApp;
window.closeAppModal = closeAppModal;
window.downloadApp = downloadApp;
window.copyTrackLink = copyTrackLink;

export default RouteDetails;